import { t } from "i18next";
import React from "react";
import { bytesToMB } from "./general";
import {
  Errors, PayloadProps, PayloadPropsExternal, SkillBook,
  SkillBookExternal
} from "../routes/Profile/AddSkillBook/AddSkillBook.types";
// import { useAuthStore } from "../stores";

type SetErrors = (errors: Errors) => void;
type SetFormValues = (callback: (prevValues: { [key: string]: unknown }) => { [key: string]: unknown }) => void;

export const handleFileChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  name: string,
  maxFileSize: number,
  minFileSize: number,
  errors: Errors,
  setErrors: SetErrors,
  setFormValues: SetFormValues
) => {
  if (e.target.files) {
    try {
      let newErrors: Errors = { ...errors };
      const file = (e.target.files as FileList)[0];

      const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
      if (!allowedTypes.includes(file.type)) { //CHECK FILE TYPE
        newErrors["file"] = "file_format_error";
        setErrors(newErrors);
        return;
      } else if (file.size < minFileSize || file.size > maxFileSize) { //CHECK FILE SIZE
        newErrors["file"] = t("upload_file_size_error", { minSize: minFileSize, maxSize: bytesToMB(maxFileSize).toFixed(2) });
        setErrors(newErrors);
        return;
      } else {
        if ("file" in newErrors) {
          delete newErrors["file"];
        }
      }

      setErrors(newErrors);

      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: file,
        "file_name": file.name
      }));
    } catch (error) {
      console.log("Error: ", error);
    }
  }
};

export const handleMultiFileChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  name: string,
  maxFileSize: number,
  minFileSize: number,
  errors: Errors,
  setErrors: SetErrors,
  setFormValues: SetFormValues
) => {
  if (e.target.files) {
    try {
      let newErrors: Errors = { ...errors };
      const files = Array.from(e.target.files);
      const validFiles: File[] = [];

      const allowedTypes = ["application/pdf", "image/jpeg", "image/png",
        "text/csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

      for(const file of files) {
        if (!allowedTypes.includes(file.type)) { //CHECK FILE TYPE
          newErrors["attachments"] = "file_format_error";
          setErrors(newErrors);
          return;
        } else if (file.size > maxFileSize) { //CHECK FILE SIZE
          newErrors["attachments"] = t("upload_file_size_error", { minSize: minFileSize, maxSize: bytesToMB(maxFileSize).toFixed(2) });
          setErrors(newErrors);
          return;
        }
        validFiles.push(file);
      }

      delete newErrors["attachments"];
      setErrors(newErrors);
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: [...(Array.isArray(prevValues[name]) ? (prevValues[name] as File[]) : []), ...validFiles]
      }));

    } catch (error) {
      console.log("Error: ", error);
    }
  }
};

export const handleMultiDelete = (
  name: string,
  fileName: string,
  setFormValues: SetFormValues
) => {
  setFormValues(prevValues => ({
    ...prevValues,
    [name]: Array.isArray(prevValues[name])
      ? (prevValues[name] as File[]).filter((file: File) => file.name !== fileName)
      : []
  }));
};

export const handleDownloadFile = async (file: File) => {
  const url = URL.createObjectURL(file);
  const downloadLink = document.createElement("a");
  downloadLink.href = url;
  downloadLink.download = file.name;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  URL.revokeObjectURL(url);
};


export const onSubmit = (data: SkillBook, mutate: (data: FormData) => void, externalTrainingId?: string | number) => {
  if (!data) return;
  let knowledgeStructure;
  let capability;
  if (data.area_domain) {
    knowledgeStructure = data.area_domain.map((area) => ({
      id: Number(area.knowledge_area.value),
      label: area.knowledge_area.label,
      domain: area.knowledge_domain
        ? [{
          id: Number(area.knowledge_domain.value),
          label: area.knowledge_domain.label
        }]
        : []
    }));
  }

  if (data.capability) {
    capability = data.capability.map((e) => ({
      id: Number(e.value),
      label: e.label
    }));
  }

  const payload: PayloadProps = {
    data: {
      uploadByAdmin: false,
      title: data.title,
      duration: data.duration,
      durationParticipation: data.durationParticipation,
      description: data.description,
      courseStartDate: data.startDate,
      courseEndDate: data.endDate,
      link: data.link,
      certificateStartValidityDate: data.validationStartDate,
      certificateEndValidityDate: data.validationEndDate,
      code: data.code,
      notes: data.notes,
      area: knowledgeStructure,
      trainingType: data.trainingType ? data.trainingType.value : "",
      capability: capability,
      sourceId: data.sourceId,
      source: data.source,
      externalTrainingId: externalTrainingId ?? undefined,
      campaign: typeof data.campaign === "object"
        ? (data.campaign as { value: string }).value
        : (data.campaign as string) ?? "",
      credits: data.credits,
      points: data.points,
      provider: data.providing_company,
      instructor: data.teacher,
      campaignId: typeof data.campaign === "object"
        ? Number((data.campaign as { value: string }).value)
        : Number(data.campaign as string) ?? null,
      customFields: data.customFields || []
    },
    certificate: data.file ? data.file : "",
    attachments: data.attachments ?? []
  };

  mutate(payload as unknown as FormData);
};

export const onSubmitExternal = (data: SkillBookExternal, mutate: (data: FormData) => void, externalTrainingId?: string | number) => {
  if (!data) return;
  let knowledgeStructure;
  let capability;
  if (data.area_domain) {
    knowledgeStructure = data.area_domain.map((area) => ({
      id: Number(area.knowledge_area.value),
      label: area.knowledge_area.label,
      domain: area.knowledge_domain
        ? [{
          id: Number(area.knowledge_domain.value),
          label: area.knowledge_domain.label
        }]
        : []
    }));
  }

  if (data.capability) {
    capability = data.capability.map((e) => ({
      id: Number(e.value),
      label: e.label
    }));
  }

  // businessTrip: oggetto { label, value }, stringa "SI"/"NO" o boolean; undefined nel flusso certificato (payload invariato).
  const rawBusinessTrip = data.businessTrip as unknown;
  const businessTripValue =
    rawBusinessTrip && typeof rawBusinessTrip === "object"
      ? (rawBusinessTrip as { value?: string }).value
      : rawBusinessTrip;
  const businessTrip =
    businessTripValue === undefined || businessTripValue === null || businessTripValue === ""
      ? undefined
      : businessTripValue === true || businessTripValue === "SI";

  // Campagna opzionale: quando non e' valorizzata, campaign/campaignId restano undefined e
  // JSON.stringify le esclude dal payload, invece di inviare ""/null che il BE rifiuta.
  const rawCampaign = data.campaign as unknown;
  const isCampaignOption = !!rawCampaign && typeof rawCampaign === "object";
  const campaign = isCampaignOption
    ? String((rawCampaign as { value: string | number }).value)
    : (rawCampaign as string) || undefined;
  // In modifica il select espone { label, value: id }; altrimenti si ricade sull'id gia' sul record.
  const rawCampaignId = isCampaignOption
    ? (rawCampaign as { value: string | number }).value
    : data.campaignId;
  const campaignId =
    rawCampaignId !== undefined && rawCampaignId !== null && rawCampaignId !== "" && !isNaN(Number(rawCampaignId))
      ? Number(rawCampaignId)
      : undefined;

  const payload: PayloadPropsExternal = {
    data: {
      uploadByAdmin: false,
      title: data.title,
      duration: data.duration,
      durationParticipation: Number(data.durationParticipation),
      description: data.description,
      courseStartDate: data.startDate,
      courseEndDate: data.endDate,
      link: data.link_certificato,
      certificateStartValidityDate: data.data_inizio_validita,
      certificateEndValidityDate: data.data_fine_validita,
      code: data.code,
      notes: data.notes,
      area: knowledgeStructure,
      businessTrip,
      trainingType: data.trainingType ? data.trainingType.value : "",
      capability: capability,
      sourceId: data.sourceId,
      source: data.source,
      externalTrainingId: externalTrainingId ?? undefined,
      status: data.status ? data.status : undefined,
      location: data.site,
      providing_company: data.providing_company,
      cost: data.cost,
      campaign,
      teacher: data.teacher,
      plan: data.plan,
      motivation: data.motivation,
      points: data.points,
      credits: data.credits,
      provider: data.provider,
      campaignId,
      customFields: data.customFields || []
    },
    certificate: data.file ? data.file : "",
    attachments: data.attachments ?? []
  };

  mutate(payload as unknown as FormData);
};
